import { Stack } from "@mui/system";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Box, Button, Card, Typography } from "@mui/material";
import { memo, useEffect, useState } from "react";
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import BludataDefaultMainHeader from "../../../UI/BludataDefaultMainHeader";
import { BluAlert, BluLoader } from "@bludata/components";
import columnsHandler from "./columns";
import fetchGruppo from "../../../api/gruppi/fetchGruppo";
import Gruppo from "../../../interfaces/gruppo";
import deleteGruppo from "../../../api/gruppi/deleteGruppo";
import { customAlphabet } from "nanoid";
import DialogModificaGruppo from "./DialogModificaGruppo";

export const ConfigurazioneGruppi = memo(() => {
  const numberAlphabet = "0123456789";
  const { loginData } = useSelector((state: RootState) => state.root);
  const [groupList, setGroupList] = useState<Gruppo[]>([]);
  const [loading, setLoading] = useState(false);
  const gridApiRef = useGridApiRef();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dialogGruppo, setDialogGruppo] = useState<{
    gruppo: Gruppo;
    isEdit: boolean;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [selectedGruppo, setSelectedGruppo] = useState<Gruppo | null>(null);

  const reloadGroup = () => {
    if (loginData?.admin) {
      setLoading(true);
      fetchGruppo()
        .then((res) => {
          setLoading(false);

          // Preserve selection if possible
          if (selectedGruppo) {
            const stillExists = res.find(
              (item) => item.id === selectedGruppo.id
            );
            if (!stillExists) {
              setSelectedGruppo(null);
            } else {
              // Update selected group with fresh data
              setSelectedGruppo(stillExists);
            }
          }

          setGroupList(res);
        })
        .catch((err) => {
          setError(err || "Internal Server Error");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    reloadGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNuovoClick = () => {
    const id = Number(customAlphabet(numberAlphabet, 10)());
    const newGruppo = {
      id: id,
      codiceGruppo: "",
      descrizione: "",
      create: true,
    };
    setDialogGruppo({
      gruppo: newGruppo,
      isEdit: false,
    });
  };

  const onEditClick = (gruppo: Gruppo) => {
    setDialogGruppo({
      gruppo,
      isEdit: true,
    });
  };

  const onDeleteClick = (gruppoId: number) => {
    setConfirmDelete(gruppoId);
  };

  const handleDeleteConfirm = () => {
    if (confirmDelete === null) return;

    setLoading(true);
    deleteGruppo(confirmDelete)
      .then(() => {
        setSuccess("Gruppo eliminato con successo.");
        reloadGroup();
      })
      .catch((err) => {
        setError(err || "Errore durante l'eliminazione");
      })
      .finally(() => {
        setLoading(false);
        setConfirmDelete(null);
      });
  };

  const onDialogClose = (savedItemId?: number) => {
    // Keep track of the ID to select after reload
    const idToSelect =
      savedItemId ||
      (dialogGruppo?.isEdit ? dialogGruppo.gruppo.id : undefined);

    reloadGroup();
    setDialogGruppo(null);

    // Select the newly created or edited group after reload
    if (idToSelect) {
      setTimeout(() => {
        fetchGruppo().then((gruppi) => {
          const gruppoToSelect = gruppi.find((g) => g.id === idToSelect);
          if (gruppoToSelect) {
            setSelectedGruppo(gruppoToSelect);
          }
        });
      }, 300); // Small delay to ensure data is loaded
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        height: "100%",
        backgroundColor: "var(--hovered-background-color)",
      }}
    >
      {dialogGruppo && (
        <DialogModificaGruppo
          onClose={onDialogClose}
          gruppo={dialogGruppo.gruppo}
          isEdit={dialogGruppo.isEdit}
        />
      )}
      <BluAlert
        show={!!success}
        handleClose={() => {
          setSuccess("");
        }}
        text={success}
        type="success"
      />
      <BluAlert
        show={!!error}
        handleClose={() => {
          setError("");
        }}
        text={error}
        type="error"
      />
      <BluAlert
        show={confirmDelete !== null}
        handleClose={() => {
          setConfirmDelete(null);
        }}
        handleConfirm={handleDeleteConfirm}
        text={
          "Sei sicuro di voler eliminare questo gruppo da Catalogo Bludata?"
        }
        type="warning"
      />
      <Stack flexDirection="column" flex={1} sx={{ height: "100%" }}>
        <BludataDefaultMainHeader
          showReload={true}
          text={"Configurazione / Gruppi"}
          onRefreshTap={reloadGroup}
        />
        <Card variant="outlined" sx={{ height: "100%", p: 1, m: 0.5, mt: 0 }}>
          <Stack direction="column" spacing={1} sx={{ height: "100%" }}>
            <BluLoader open={loading} />
            <Typography>
              In questa sezione è possibile aggiungere dei nuovi gruppi. I campi
              Codice Gruppo e Descrizione sono obbligatori.
            </Typography>
            {loginData?.admin && (
              <>
                <DataGridPremium
                  apiRef={gridApiRef}
                  columns={columnsHandler()}
                  rows={!!groupList ? groupList : []}
                  onRowClick={(params) =>
                    setSelectedGruppo(params.row as Gruppo)
                  }
                  getRowClassName={(params) =>
                    selectedGruppo && params.id === selectedGruppo.id
                      ? "selected-row"
                      : ""
                  }
                  sx={{
                    "& .MuiDataGrid-cell": {
                      outline: "none!important",
                    },
                    "& .MuiInputBase-root": {
                      border: "none!important",
                    },
                    "& .selected-row": {
                      backgroundColor: "rgba(0, 0, 255, 0.04)",
                    },
                    mt: 1,
                  }}
                />
              </>
            )}
            <Stack direction={"row"} justifyContent={"end"} spacing={0.5}>
              <Button
                variant="contained"
                color="primary"
                onClick={onNuovoClick}
              >
                Nuovo
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => selectedGruppo && onEditClick(selectedGruppo)}
                disabled={!selectedGruppo}
              >
                Modifica
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() =>
                  selectedGruppo &&
                  selectedGruppo.id &&
                  onDeleteClick(selectedGruppo.id)
                }
                disabled={!selectedGruppo}
              >
                Elimina
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    </Box>
  );
});
